import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, useWindowDimensions } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import PropTypes from 'prop-types';
import styles, { colors } from '../../style/rulesEngine';
import { ACTION_TYPE_OPTIONS } from './cascadeConfig';
import RuleEngineDropdown from './RuleEngineDropdown';
import TagPickerModal from './TagPickerModal';
import SKUPickerModal from './SKUPickerModal';

const ActionRow = ({ action, index, onChange, onRemove, isOnly, errors }) => {
  const { width } = useWindowDimensions();
  const isNarrow = width < 600;
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showSkuPicker, setShowSkuPicker] = useState(false);

  const handleTypeChange = (newType) => {
    const defaultValue = newType === 'add_item' ? { skus: [], quantity: 1 } : {};
    onChange(index, { type: newType, value: defaultValue });
  };

  const handleTagSelected = (tagId, tagName) => {
    onChange(index, { ...action, value: { tag_id: tagId, tag_name: tagName } });
  };

  const handleItemField = (field, val) => {
    onChange(index, { ...action, value: { ...(action.value || {}), [field]: val } });
  };

  const handleSkuSelected = (skus) => {
    onChange(index, {
      ...action,
      value: {
        ...(action.value || {}),
        skus: Array.isArray(skus) ? skus : (skus ? [skus] : []),
      },
    });
  };

  const savedSkus = action.value?.skus
    ? action.value.skus
    : (action.value?.sku ? [action.value.sku] : []);
  const skuDisplayLabel = savedSkus.length === 0
    ? ''
    : savedSkus.length === 1
      ? savedSkus[0]
      : `${savedSkus[0]} +${savedSkus.length - 1} more`;

  const typeError = errors?.[`action_${index}_type`];
  const valueError = errors?.[`action_${index}_value`];
  const skuError = errors?.[`action_${index}_sku`];
  const qtyError = errors?.[`action_${index}_qty`];

  const rowStyle = [styles.actionRow, isNarrow && { flexDirection: 'column', flexWrap: 'nowrap', gap: 8, alignItems: 'stretch' }];
  const fieldStyle = [styles.rowField, isNarrow && { flexGrow: 0, flexShrink: 0, flexBasis: 'auto', width: '100%' }];

  return (
    <View style={rowStyle}>
      {/* Action type dropdown */}
      <View style={fieldStyle}>
        <Text style={styles.fieldLabel}>Action</Text>
        <RuleEngineDropdown
          options={ACTION_TYPE_OPTIONS}
          selectedValue={action.type}
          onSelect={handleTypeChange}
          placeholder="Select action"
          testID={`action_${index}_type`}
          error={!!typeError}
        />
        {typeError && <Text style={styles.errorText}>{typeError}</Text>}
      </View>

      {/* Add Tag — tag picker modal */}
      {action.type === 'add_tag' && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Tag</Text>
          {action.value?.tag_name ? (
            <View style={styles.tagChip}>
              <Text style={styles.tagChipText}>{action.value.tag_name}</Text>
              <TouchableOpacity onPress={() => onChange(index, { ...action, value: {} })}>
                <AntDesign name="close" size={12} color={colors.accent} />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              testID={`action_${index}_tag_picker`}
              onPress={() => setShowTagPicker(true)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottomWidth: 1.5,
                borderBottomColor: valueError ? colors.danger : colors.textMuted,
                paddingVertical: 7,
                paddingHorizontal: 2,
              }}
            >
              <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 12, color: colors.textMuted }}>
                Select tag
              </Text>
              <AntDesign name="down" size={11} color={colors.textMuted} style={{ marginLeft: 4 }} />
            </TouchableOpacity>
          )}
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}

          <TagPickerModal
            visible={showTagPicker}
            onClose={() => setShowTagPicker(false)}
            title="Select Tag to Add"
            selectedTagId={action.value?.tag_id || null}
            onTagSelected={handleTagSelected}
          />
        </View>
      )}

      {/* Add Item — SKU, Qty, Name */}
      {action.type === 'add_item' && (
        <View style={isNarrow ? { width: '100%', gap: 4 } : { flex: 2, gap: 4 }}>
          <View>
            <Text style={styles.fieldLabel}>SKU</Text>
            <TouchableOpacity
              testID={`action_${index}_sku_picker`}
              onPress={() => setShowSkuPicker(true)}
              style={[styles.nameInput, skuError && styles.errorBorder, { justifyContent: 'center' }]}
            >
              <Text style={{ color: skuDisplayLabel ? colors.text : colors.textMuted }}>
                {skuDisplayLabel || 'Select products…'}
              </Text>
            </TouchableOpacity>
            {skuError && <Text style={styles.errorText}>{skuError}</Text>}
            <SKUPickerModal
              visible={showSkuPicker}
              onClose={() => setShowSkuPicker(false)}
              selectedSkus={savedSkus}
              onSKUsSelected={handleSkuSelected}
            />
          </View>
          <View>
            <Text style={styles.fieldLabel}>Qty</Text>
            <TextInput
              style={[styles.nameInput, qtyError && styles.errorBorder]}
              value={String(action.value?.quantity || 1)}
              onChangeText={(val) => handleItemField('quantity', parseInt(val, 10) || '')}
              placeholder="Qty"
              placeholderTextColor={colors.textMuted}
              keyboardType="numeric"
            />
            {qtyError && <Text style={styles.errorText}>{qtyError}</Text>}
          </View>
        </View>
      )}

      {/* Remove button */}
      {!isOnly && (
        <TouchableOpacity onPress={() => onRemove(index)} style={styles.removeButton}>
          <i className="icon-trash" style={{ color: '#FFFFFF', fontSize: 20 }} />
        </TouchableOpacity>
      )}
    </View>
  );
};

ActionRow.propTypes = {
  action: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isOnly: PropTypes.bool.isRequired,
  errors: PropTypes.object,
};

export default ActionRow;
